import React, { useState, useEffect } from 'react';
import { TextField, FormControl, Switch, FormGroup, FormControlLabel, CircularProgress, Box } from '@mui/material';
import ImageContainerPreview from '../../Common/ImageContainerPreview';
import { useDispatch, useSelector } from 'react-redux';
import { uploadImage, clearMedia } from '../../../actions/mediaActions';
import FieldSetGroup from '../../Elements/FieldSetGroup';
import ParentSelector from '../../Common/ParentSelector';
import UniversalLinkField from '../../Common/UniversalLinkField';

const DeckForm = ({ deck, setDeck }) => {
  const dispatch = useDispatch();

  const mediaStore = useSelector((state) => state.media);
  const categoryStore = useSelector((state) => state.categories);
  const subcategoryStore = useSelector(state => state.subcategories);

  const [isEdited, setIsEdited] = useState(false);

  useEffect(() => {
    if (mediaStore.image) {
      deck[mediaStore.target] = mediaStore.image;
      dispatch(clearMedia());
      handleChange({ target: { name: mediaStore.target, value: mediaStore.image } });
    }
  }, [mediaStore]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    dispatch(uploadImage(file, e.target.name));
  };

  const removeImage = (e) => {
    handleChange({ target: { name: e.target.name, value: "" } });
  }

  const handleSwitchChange = (e) => {
    handleChange({ target: { name: e.target.name, value: e.target.checked } });
  }

  const handleChange = (e) => {
    setDeck({ ...deck, [e.target.name]: e.target.value });
  };

  const handleParentChange = (value) => {
    if (!isEdited) setIsEdited(true);

    if (value === "0") {
      setDeck({
        ...deck,
        category_id: null,
        subcategory_id: null
      });
      return;
    }

    const [id, type] = value.split(/(?<=[0-9])(?=[A-Z])/);

    if (type === "CAT") {
      setDeck({
        ...deck,
        category_id: id,
        subcategory_id: null
      });
      return;
    }

    if (type === "SUB" || type === "AUD") {
      setDeck({
        ...deck,
        category_id: null,
        subcategory_id: id
      });
      return;
    }
  };

  const getParentValue = () => {
    if (deck.category_id) {
      return String(deck.category_id) + "CAT";
    }
    if (deck.subcategory_id) {
      return String(deck.subcategory_id) + "SUB";
    }
    return "0";
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      <TextField
        fullWidth
        label="Name"
        variant="outlined"
        name="name"
        value={deck.name ?? ""}
        onChange={handleChange}
        autoFocus
        required
      />

      <Box sx={{ display: 'flex', gap: 2 }}>
        <FormControl sx={{ flex: 3 }}>
          <ParentSelector
            value={getParentValue()}
            onChange={handleParentChange}
            label="Category/Subcategory"
            allowedTypes={['category', 'subcategory', 'audiobook']}
            showQuickTaps={true}
          />
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2, flex: 2 }}>
          <TextField
            sx={{ width: '120px' }}
            label="Primary color"
            variant="outlined"
            type="color"
            name="primary_color_hex"
            value={deck.primary_color_hex}
            onChange={handleChange}
            slotProps={{
              inputLabel: {
                shrink: true
              }
            }}
          />
          <TextField
            sx={{ width: '120px' }}
            label="Free View Limit"
            variant="outlined"
            name="free_view_limit"
            value={deck.free_view_limit}
            onChange={handleChange}
          />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormGroup>
              <FormControlLabel
                control={<Switch aria-label="is premium" checked={Boolean(deck.is_premium)} onChange={handleSwitchChange} name="is_premium" />}
                label="Is Premium?"
              />
            </FormGroup>
            <FormGroup>
              <FormControlLabel
                control={<Switch aria-label="is active" checked={Boolean(deck.is_active)} onChange={handleSwitchChange} name="is_active" />}
                label="Is Active?"
              />
            </FormGroup>
          </Box>
        </Box>
      </Box>

      <FieldSetGroup title="Deck Descriptions">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Module Description"
            variant="outlined"
            name="module_description"
            multiline
            minRows={3}
            value={deck.module_description ?? ""}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            label="Choose Card Screen Description"
            variant="outlined"
            name="description"
            multiline
            minRows={3}
            value={deck.description ?? ""}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            label="See All - Description"
            variant="outlined"
            multiline
            minRows={3}
            name="see_all_description"
            value={deck.see_all_description ?? ""}
            onChange={handleChange}
          />
        </Box>
      </FieldSetGroup>

      <FieldSetGroup title="Sharing">
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <ImageContainerPreview
              title={"Share Image"}
              image={deck.share_image_url}
              name="share_image_url"
              handleRemove={removeImage}
              handleImageUpload={handleImageUpload}
              showCopyUrlButton={true}
              sx={{ padding: 5, background: '#FFFCF5', height: 250, objectFit: 'contain' }}
            />
          </Box>
          <Box sx={{ flex: 2 }}>
            <UniversalLinkField url={deck.universal_link} />
          </Box>
        </Box>
      </FieldSetGroup>

      <FieldSetGroup title="Purchase Store">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Purchase Store URL"
            variant="outlined"
            name="purchase_store_url"
            value={deck.purchase_store_url ?? ""}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            label="Purchase Store Text"
            variant="outlined"
            multiline
            minRows={3}
            name="purchase_store_text"
            value={deck.purchase_store_text ?? ""}
            onChange={handleChange}
          />
        </Box>
      </FieldSetGroup>

      <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ImageContainerPreview
            title={"Module Image"}
            image={deck.module_image_url}
            name="module_image_url"
            handleRemove={removeImage}
            handleImageUpload={handleImageUpload}
            sx={{ padding: 5, background: '#FFFCF5', height: 250, objectFit: 'contain', width: '100%' }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ImageContainerPreview
            title={"Header Image"}
            image={deck.header_image_url}
            name="header_image_url"
            handleRemove={removeImage}
            handleImageUpload={handleImageUpload}
            sx={{ padding: 5, background: '#FFFCF5', height: 250, objectFit: 'contain', width: '100%' }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ImageContainerPreview
            title={"Wrapper Image"}
            image={deck.wrapper_image_url}
            name="wrapper_image_url"
            handleRemove={removeImage}
            handleImageUpload={handleImageUpload}
            sx={{ padding: 5, background: '#FFFCF5', height: 250, objectFit: 'contain', width: '100%' }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ImageContainerPreview
            title={"Search Result Image"}
            image={deck.search_result_image_url}
            name="search_result_image_url"
            handleRemove={removeImage}
            handleImageUpload={handleImageUpload}
            sx={{ padding: 5, background: '#FFFCF5', height: 250, objectFit: 'contain', width: '100%' }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DeckForm;
